import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProjects, createProject, updateProject, deleteProject, getClients } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, FolderKanban, Loader2, Clock, DollarSign, CheckCircle, Pause, XCircle, ChevronRight, Search, TrendingUp, Users, CalendarDays, Trash2, Pencil, LayoutGrid, Columns3 } from 'lucide-react';
import { toast } from 'sonner';

const statusConfig = {
  active: { label: 'En cours', color: 'bg-emerald-100 text-emerald-700', icon: Clock },
  paused: { label: 'En pause', color: 'bg-amber-100 text-amber-700', icon: Pause },
  completed: { label: 'Termine', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  cancelled: { label: 'Annule', color: 'bg-slate-100 text-slate-500', icon: XCircle },
};

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [draggedProject, setDraggedProject] = useState(null);
  const navigate = useNavigate();

  const defaultForm = { name: '', description: '', client_id: '', status: 'active', start_date: '', end_date: '', budget: 0, hourly_rate: 0, currency: 'CHF', color: '#3b82f6' };
  const [form, setForm] = useState(defaultForm);

  useEffect(() => { loadData(); }, [statusFilter]);

  const loadData = async () => {
    try {
      const [projRes, clientRes] = await Promise.all([getProjects(statusFilter !== 'all' ? statusFilter : null), getClients()]);
      setProjects(projRes.data);
      setClients(clientRes.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, description: p.description || '', client_id: p.client_id || '', status: p.status, start_date: p.start_date || '', end_date: p.end_date || '', budget: p.budget || 0, hourly_rate: p.hourly_rate || 0, currency: p.currency || 'CHF', color: p.color || '#3b82f6' });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      if (editing) {
        await updateProject(editing.id, form);
        toast.success('Projet mis a jour');
      } else {
        await createProject(form);
        toast.success('Projet cree');
      }
      setDialogOpen(false);
      setEditing(null);
      setForm(defaultForm);
      loadData();
    } catch (err) { toast.error('Erreur'); } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await deleteProject(projectToDelete);
      toast.success('Projet supprime');
      setDeleteDialogOpen(false);
      loadData();
    } catch (err) { toast.error('Erreur'); }
  };

  const fmt = (amount, currency = 'CHF') => new Intl.NumberFormat('fr-CH', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount) + ' ' + currency;

  const filtered = projects.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()) || (p.client_name || '').toLowerCase().includes(searchTerm.toLowerCase()));

  // Summary stats
  const totalBudget = projects.reduce((s, p) => s + (p.budget || 0), 0);
  const totalInvoiced = projects.reduce((s, p) => s + (p.total_invoiced || 0), 0);
  const totalHours = projects.reduce((s, p) => s + (p.total_hours || 0), 0);
  const activeCount = projects.filter(p => p.status === 'active').length;
  const totalCost = projects.reduce((s, p) => s + (p.total_expenses || 0) + (p.time_cost || 0), 0);
  const totalProfit = totalInvoiced - totalCost;

  const handleDrop = async (projectId, newStatus) => {
    try {
      await updateProject(projectId, { status: newStatus });
      toast.success('Statut mis a jour');
      loadData();
    } catch (err) { toast.error('Erreur'); }
    setDraggedProject(null);
  };

  const kanbanColumns = [
    { status: 'active', label: 'En cours', color: 'border-emerald-400', bg: 'bg-emerald-50', dot: 'bg-emerald-500' },
    { status: 'paused', label: 'En pause', color: 'border-amber-400', bg: 'bg-amber-50', dot: 'bg-amber-500' },
    { status: 'completed', label: 'Termine', color: 'border-blue-400', bg: 'bg-blue-50', dot: 'bg-blue-500' },
    { status: 'cancelled', label: 'Annule', color: 'border-slate-300', bg: 'bg-slate-50', dot: 'bg-slate-400' },
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Projets</h1>
          <p className="text-slate-500 mt-1">Gerez vos projets, taches et temps</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(defaultForm); setDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="new-project-btn">
          <Plus className="w-4 h-4 mr-2" />Nouveau projet
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-slate-200"><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1"><FolderKanban className="w-4 h-4 text-blue-500" /><span className="text-xs text-slate-500">Projets actifs</span></div>
          <p className="text-2xl font-bold">{activeCount}</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1"><DollarSign className="w-4 h-4 text-emerald-500" /><span className="text-xs text-slate-500">Budget total</span></div>
          <p className="text-2xl font-bold">{fmt(totalBudget)}</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1"><TrendingUp className="w-4 h-4 text-green-500" /><span className="text-xs text-slate-500">Total facture</span></div>
          <p className="text-2xl font-bold">{fmt(totalInvoiced)}</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1"><DollarSign className="w-4 h-4 text-indigo-500" /><span className="text-xs text-slate-500">Profit total</span></div>
          <p className={`text-2xl font-bold ${totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmt(totalProfit)}</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1"><Clock className="w-4 h-4 text-orange-500" /><span className="text-xs text-slate-500">Heures totales</span></div>
          <p className="text-2xl font-bold">{totalHours}h</p>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Rechercher..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="active">En cours</SelectItem>
            <SelectItem value="paused">En pause</SelectItem>
            <SelectItem value="completed">Termines</SelectItem>
            <SelectItem value="cancelled">Annules</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex bg-slate-100 rounded-lg p-0.5">
          <button onClick={() => setViewMode('grid')} className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 transition-colors ${viewMode === 'grid' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-testid="view-grid-btn">
            <LayoutGrid className="w-3.5 h-3.5" />Grille
          </button>
          <button onClick={() => setViewMode('kanban')} className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 transition-colors ${viewMode === 'kanban' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-testid="view-kanban-btn">
            <Columns3 className="w-3.5 h-3.5" />Kanban
          </button>
        </div>
      </div>

      {/* Project Cards - Grid View */}
      {viewMode === 'grid' && filtered.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(p => {
            const sc = statusConfig[p.status] || statusConfig.active;
            const StatusIcon = sc.icon;
            const budgetPct = p.budget > 0 ? Math.min(100, ((p.total_expenses + p.time_cost) / p.budget) * 100) : 0;
            return (
              <Card key={p.id} className="border-slate-200 hover:shadow-md transition-shadow cursor-pointer group" data-testid={`project-card-${p.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color }} />
                      <h3 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors" onClick={() => navigate(`/app/projects/${p.id}`)}>{p.name}</h3>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sc.color}`}>{sc.label}</span>
                    </div>
                  </div>
                  {p.client_name && <p className="text-xs text-slate-500 mb-3"><Users className="w-3 h-3 inline mr-1" />{p.client_name}</p>}
                  
                  {/* Progress bar */}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                      <span>Avancement</span>
                      <span>{p.progress}%</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${p.progress}%`, backgroundColor: p.color }} />
                    </div>
                  </div>

                  {/* Budget bar */}
                  {p.budget > 0 && (
                    <div className="mb-3">
                      <div className="flex justify-between text-xs text-slate-500 mb-1">
                        <span>Budget</span>
                        <span>{fmt(p.total_expenses + p.time_cost)} / {fmt(p.budget)}</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${budgetPct > 90 ? 'bg-red-500' : budgetPct > 70 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${budgetPct}%` }} />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                    <div className="flex gap-3 text-xs text-slate-500">
                      <span><Clock className="w-3 h-3 inline mr-0.5" />{p.total_hours}h</span>
                      <span>{p.tasks_done}/{p.tasks_total} taches</span>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); openEdit(p); }}><Pencil className="w-3 h-3" /></Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={(e) => { e.stopPropagation(); setProjectToDelete(p.id); setDeleteDialogOpen(true); }}><Trash2 className="w-3 h-3" /></Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => navigate(`/app/projects/${p.id}`)}><ChevronRight className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Kanban View */}
      {viewMode === 'kanban' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kanban-board">
          {kanbanColumns.map(col => {
            const colProjects = filtered.filter(p => p.status === col.status);
            return (
              <div key={col.status}
                className={`rounded-xl border-2 border-dashed ${draggedProject ? col.color : 'border-slate-200'} transition-colors min-h-[200px]`}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('bg-slate-50'); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove('bg-slate-50'); }}
                onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('bg-slate-50'); if (draggedProject) handleDrop(draggedProject, col.status); }}
                data-testid={`kanban-col-${col.status}`}
              >
                <div className={`px-3 py-2.5 rounded-t-lg ${col.bg} flex items-center justify-between`}>
                  <div className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${col.dot}`} />
                    <span className="text-sm font-semibold text-slate-700">{col.label}</span>
                  </div>
                  <span className="text-xs font-medium text-slate-500 bg-white/80 px-1.5 py-0.5 rounded">{colProjects.length}</span>
                </div>
                <div className="p-2 space-y-2">
                  {colProjects.map(p => {
                    const budgetPct = p.budget > 0 ? Math.min(100, ((p.total_expenses + p.time_cost) / p.budget) * 100) : 0;
                    return (
                      <div key={p.id}
                        draggable
                        onDragStart={() => setDraggedProject(p.id)}
                        onDragEnd={() => setDraggedProject(null)}
                        className="bg-white border border-slate-200 rounded-lg p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow group"
                        data-testid={`kanban-card-${p.id}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
                          <h4 className="text-sm font-semibold text-slate-900 truncate group-hover:text-blue-600 cursor-pointer" onClick={() => navigate(`/app/projects/${p.id}`)}>{p.name}</h4>
                        </div>
                        {p.client_name && <p className="text-[10px] text-slate-400 mb-2 truncate">{p.client_name}</p>}
                        {/* Mini progress */}
                        <div className="h-1 bg-slate-100 rounded-full overflow-hidden mb-2">
                          <div className="h-full rounded-full" style={{ width: `${p.progress}%`, backgroundColor: p.color }} />
                        </div>
                        <div className="flex items-center justify-between text-[10px] text-slate-500">
                          <span>{p.progress}%</span>
                          <span><Clock className="w-2.5 h-2.5 inline mr-0.5" />{p.total_hours}h</span>
                          <span>{p.tasks_done}/{p.tasks_total}</span>
                        </div>
                        {p.budget > 0 && (
                          <div className="mt-1.5 text-[10px] text-slate-400">{fmt(p.total_expenses + p.time_cost)} / {fmt(p.budget)}</div>
                        )}
                      </div>
                    );
                  })}
                  {colProjects.length === 0 && (
                    <div className="text-center py-6 text-xs text-slate-300">
                      Glissez un projet ici
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {viewMode === 'grid' && filtered.length === 0 && (
        <Card className="border-slate-200"><CardContent className="py-12 text-center">
          <FolderKanban className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-1">Aucun projet</h3>
          <p className="text-slate-500 mb-4">Creez votre premier projet</p>
          <Button onClick={() => { setForm(defaultForm); setDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-2" />Nouveau projet</Button>
        </CardContent></Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader><DialogTitle>{editing ? 'Modifier le projet' : 'Nouveau projet'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Nom *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="project-name" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Client</Label>
                <Select value={form.client_id || "none"} onValueChange={(v) => setForm({ ...form, client_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Statut</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">En cours</SelectItem>
                    <SelectItem value="paused">En pause</SelectItem>
                    <SelectItem value="completed">Termine</SelectItem>
                    <SelectItem value="cancelled">Annule</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Date debut</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div><Label>Date fin</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Budget (CHF)</Label><Input type="number" min="0" value={form.budget} onChange={(e) => setForm({ ...form, budget: parseFloat(e.target.value) || 0 })} onFocus={(e) => e.target.select()} data-testid="project-budget" /></div>
              <div><Label>Taux horaire (CHF/h)</Label><Input type="number" min="0" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: parseFloat(e.target.value) || 0 })} onFocus={(e) => e.target.select()} data-testid="project-rate" /></div>
            </div>
            <div><Label>Couleur</Label><div className="flex gap-2 mt-1">
              {['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#06b6d4','#f97316'].map(c => (
                <button key={c} type="button" onClick={() => setForm({ ...form, color: c })} className={`w-7 h-7 rounded-full border-2 transition-all ${form.color === c ? 'border-slate-900 scale-110' : 'border-transparent'}`} style={{ backgroundColor: c }} />
              ))}
            </div></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-project-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}{editing ? 'Mettre a jour' : 'Creer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent><AlertDialogHeader>
          <AlertDialogTitle>Supprimer ce projet ?</AlertDialogTitle>
          <AlertDialogDescription>Toutes les taches et entrees de temps seront supprimees.</AlertDialogDescription>
        </AlertDialogHeader><AlertDialogFooter>
          <AlertDialogCancel>Annuler</AlertDialogCancel>
          <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
        </AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProjectsPage;
